import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { CometChatUIKit } from '@cometchat/chat-uikit-react';
import { ROUTE } from 'route/RouteEnums';
import { AuthContext } from 'utils/AuthContext';
import { SiblingProfile } from './siblingProfile';

/** Longest wait for the CometChat logout before the switch carries on without it. */
const COMET_LOGOUT_TIMEOUT_MS = 3000;

/**
 * MM-1123: logs the current CometChat user out without ever blocking a profile switch: a failed or
 * hung logout is logged and ignored after COMET_LOGOUT_TIMEOUT_MS.
 */
const logoutCometChatSafely = async (): Promise<void> => {
  try {
    await Promise.race([
      CometChatUIKit.logout(),
      new Promise((resolve) => setTimeout(resolve, COMET_LOGOUT_TIMEOUT_MS)),
    ]);
  } catch (err) {
    console.log('Comet logout failed during patient switch', err);
  }
};

/**
 * MM-1123: switches the open profile to another child (CometChat logout, then patientId /
 * secureMessageUserId swapped on the user) and lands the guardian on the dashboard. CometChat is
 * best effort: if its logout or the new login fails, the profile still switches and the guardian
 * still lands on the dashboard.
 *
 * @returns a function that switches to the given sibling.
 */
export const useSwitchSibling = () => {
  const navigate = useNavigate();
  const { user, setUserDetails } = useContext(AuthContext);

  /**
   * @param sibling - child to open.
   * @param confirm - MM-1172: show the "Now viewing" confirmation even when the child is already
   *                  open (e.g. picked on the post sign-in patient picker).
   */
  return async (sibling: SiblingProfile, confirm = false) => {
    const switched = sibling.id !== user?.patientId;
    if (switched) {
      await logoutCometChatSafely();
      // Not awaited: setUserDetails switches the profile first, then waits on the CometChat login.
      Promise.resolve(
        setUserDetails({ ...user, patientId: sibling.id, secureMessageUserId: sibling.cometUserId })
      ).catch((err: any) => console.log('Comet login failed during patient switch', err));
    }
    // Option A: the dashboard greeting reads switchedSibling to confirm "Now viewing <child>".
    navigate(ROUTE.DASHBOARD, { state: { switchedSibling: switched || confirm } });
  };
};
